import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from '@/api/index.js'
import { purgeApiCaches } from '@/sw/purgeCaches.js'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const status = ref('unknown')
  const csrfToken = ref(null)
  let pendingMe = null

  const isAuthenticated = computed(() => status.value === 'authenticated')

  function adopt({ user: nextUser, csrfToken: nextToken }) {
    user.value = nextUser
    status.value = 'authenticated'
    if (nextToken) {
      csrfToken.value = nextToken
      api.http.setCsrfToken(nextToken)
    }
  }

  function clear() {
    user.value = null
    status.value = 'anonymous'
    csrfToken.value = null
    api.http.setCsrfToken(null)
  }

  async function signup(body) {
    adopt(await api.auth.signup(body))
  }

  async function login(body) {
    adopt(await api.auth.login(body))
  }

  async function logout() {
    try {
      await api.auth.logout()
    } catch {
      // A user who clicked sign-out must end up signed out locally regardless of
      // whether the request reached the server — fall through to clearing state.
    } finally {
      // purgeApiCaches() never rejects by contract (see sw/purgeCaches.js), but the
      // `.catch()` is deliberate defense in depth: a mocked or future misbehaving purge
      // must still never stop `clear()` from running, or a user who clicks sign-out ends
      // up looking authenticated with no way to tell.
      await purgeApiCaches().catch(() => {})
      clear()
    }
  }

  function fetchMe() {
    if (pendingMe) return pendingMe
    pendingMe = api.auth
      .me()
      .then(({ user: nextUser }) => {
        user.value = nextUser
        status.value = 'authenticated'
      })
      .catch(() => {
        clear()
      })
      .finally(() => {
        pendingMe = null
      })
    return pendingMe
  }

  async function updateProfile(body) {
    const { user: nextUser } = await api.auth.updateProfile(body)
    user.value = nextUser
  }

  function handleUnauthorized() {
    // The server ended the session out from under us (expired/rotated cookie, forced
    // sign-out elsewhere) -- exactly the moment cached personal data (the api-goals
    // NetworkFirst cache) must stop answering as if the user were still signed in.
    // Fire-and-forget: existing callers (http.js's onUnauthorized) rely on state clearing
    // synchronously, so the purge runs alongside that, not gating it -- and the `.catch()`
    // exists because a rejection here would otherwise be an unhandled promise rejection.
    purgeApiCaches().catch(() => {})
    clear()
  }

  return {
    user,
    status,
    csrfToken,
    isAuthenticated,
    signup,
    login,
    logout,
    fetchMe,
    updateProfile,
    handleUnauthorized,
    clear,
  }
})
